<template>
  <div class="patient">
    <app-query-bar
      v-model="queryData"
      :queryConfig="queryConfig"
      @onSearch="fetchPatients">
      <template slot="ageRange">
        <app-spacing
          class="age-range"
          spacing="sm">
          <div>
            <InputNumber
              :max="150"
              :min="0"
              v-model="queryData.minAge" />
            岁
          </div>
          <div>至</div>
          <div>
            <InputNumber
              :max="150"
              :min="0"
              v-model="queryData.maxAge" />
            岁
          </div>
        </app-spacing>
      </template>
      <template slot="visualRange">
        <app-spacing
          class="visual-range"
          spacing="sm">
          <Select
            style="width: 100px"
            filterable
            clearable
            v-model="queryData.minVisual">
            <Option
              v-for="option in VISUAL_RANGE_OPTIONS"
              :value="option.value"
              :key="option.value">
              {{ option.label }}
            </Option>
          </Select>
          <div>至</div>
          <Select
            style="width: 100px"
            filterable
            clearable
            v-model="queryData.maxVisual">
            <Option
              v-for="option in VISUAL_RANGE_OPTIONS"
              :value="option.value"
              :key="option.value">
              {{ option.label }}
            </Option>
          </Select>
        </app-spacing>
      </template>
    </app-query-bar>
    <!-- <app-export-bar
      :show-export-all="false"
      @onExport="onExport"
      @onExportAll="onExportAll" /> -->
    <confirm-export-bar :show-export-all="false" :exportFn="onExport" :exportAllFn="onExportAll"/>
    <app-data-table
      :columns="PATIENT_COLUMNS"
      :allowed-permissions="Object.values(ALLOWED_PERMISSION)"
      :data="patients"
      v-model="queryData"
      @onPageConfigChange="fetchPatients">
      <template
        slot="sex"
        slot-scope="{ row }">
        {{ SEX_TEXT[row.sex] }}
      </template>
      <template
        slot="patientType"
        slot-scope="{ row }">
        {{ PATIENT_TYPE_TEXT[row.patient_type] }}
      </template>
      <template
        slot="type"
        slot-scope="{ row }">
        {{ CUSTOMER_TYPE_TEXT[row.type] }}
      </template>
      <template
        slot="corrected_eye_vision"
        slot-scope="{ row }">
        {{ getHealthRecordPair(
          row.health_record,
          'corrected_eye_right_vision',
          'corrected_eye_left_vision',
          '',
        ) }}
      </template>
      <template
        slot="diop"
        slot-scope="{ row }">
        {{ getHealthRecordPair(row.health_record, 'diopOd', 'diopOs', 'D') }}
      </template>
      <template
        slot="axial"
        slot-scope="{ row }">
        {{ getHealthRecordPair(row.health_record, 'axialOd', 'axialOs', 'mm') }}
      </template>
      <template
        slot="cylAxis"
        slot-scope="{ row }">
        {{ getCylinderAxisPair(row.health_record) }}
      </template>
      <template
        slot="pupilDistance"
        slot-scope="{ row }">
        {{ getPupilDistance(row.health_record) }}
      </template>
      <template
        slot="geneticStatus"
        slot-scope="{ row }">
        {{ GENETIC_STATUS_TEXT[row.is_genetic] }}
      </template>
      <div
        class="eyes-position"
        :title="EYES_POSITION_TEXT[row.eye_position]"
        slot="eyesPosition"
        slot-scope="{ row }">
        {{ EYES_POSITION_TEXT[row.eye_position] }}
      </div>
      <div
        class="patient-demand"
        :title="getPatientDemandTexts(row.self_describe)"
        slot="patientDemand"
        slot-scope="{ row }">
        {{ getPatientDemandTexts(row.self_describe) }}
      </div>
      <div
        class="treatment-history"
        :title="getTreatmentHistoryTexts(row.cure_history)"
        slot="treatmentHistory"
        slot-scope="{ row }">
        {{ getTreatmentHistoryTexts(row.cure_history) }}
      </div>
      <!-- <div
        class="treatment-history"
        :title="getTreatmentHistoryTexts(row.cure_history)"
        slot="actiontest"
        slot-scope="{ row }">
        {{ getTreatmentHistoryTexts(row.cure_history) }}
      </div> -->
      <app-spacing
        class="table-action"
        slot-scope="{ row }"
        slot="action">
        <Button
          v-if="permissions[PATIENT_ACTION.LOG]"
          type="primary"
          @click="() => handlePatientChart(row)">
          查看
        </Button>
      </app-spacing>
    </app-data-table>

    <!-- 趋势图弹框 -->
    <app-modal
      :value="patientChartModalConfig.visible"
      :title="`${patientChartModalConfig.nickName}的既往视力`"
      height="60%"
      width="80%"
      :footerHide="true"
      @on-cancel="() => patientChartModalConfig.visible = false">
      <div class="patient-chart-type-selector">
        <Button
          v-for="option in PATIENT_CHART_TYPE_OPTIONS"
          :key="option.value"
          :type="patientChartModalConfig.selectedType === option.value ? 'primary' : 'default'"
          @click="() => handlePatientChartTypeChange(option.value)">
          {{ option.label }}
        </Button>
      </div>
      <div id="patientChart"
        v-if="patientChartModalConfig.visible"
        style="width:100%;height:400px">
      </div>
      <el-table
        :key="patientChartModalConfig.selectedType"
        :data="selectedPatientChartData"
        stripe
        border
        style="width: 100%">
        <el-table-column
          align="center"
          prop="create_time"
          label="日期">
        </el-table-column>
        <el-table-column
          v-if="patientChartModalConfig.selectedType === PATIENT_CHART_TYPE.NAKED_EYE"
          align="center"
          prop="naked_eye_left_vision"
          label="裸眼视力左">
        </el-table-column>
        <el-table-column
          v-if="patientChartModalConfig.selectedType === PATIENT_CHART_TYPE.NAKED_EYE"
          align="center"
          prop="naked_eye_right_vision"
          label="裸眼视力右">
        </el-table-column>
        <el-table-column
          v-if="patientChartModalConfig.selectedType === PATIENT_CHART_TYPE.DIOP"
          align="center"
          prop="diopOs"
          label="屈光度左">
          <template slot-scope="{ row }">
            {{ formatHealthRecordValue(row.diopOs, 'D') }}
          </template>
        </el-table-column>
        <el-table-column
          v-if="patientChartModalConfig.selectedType === PATIENT_CHART_TYPE.DIOP"
          align="center"
          prop="diopOd"
          label="屈光度右">
          <template slot-scope="{ row }">
            {{ formatHealthRecordValue(row.diopOd, 'D') }}
          </template>
        </el-table-column>
        <el-table-column
          v-if="patientChartModalConfig.selectedType === PATIENT_CHART_TYPE.AXIAL"
          align="center"
          prop="axialOs"
          label="眼轴左">
          <template slot-scope="{ row }">
            {{ formatHealthRecordValue(row.axialOs, 'mm') }}
          </template>
        </el-table-column>
        <el-table-column
          v-if="patientChartModalConfig.selectedType === PATIENT_CHART_TYPE.AXIAL"
          align="center"
          prop="axialOd"
          label="眼轴右">
          <template slot-scope="{ row }">
            {{ formatHealthRecordValue(row.axialOd, 'mm') }}
          </template>
        </el-table-column>
        <el-table-column
          v-if="patientChartModalConfig.selectedType === PATIENT_CHART_TYPE.CORRECTED_EYE"
          align="center"
          prop="corrected_eye_left_vision"
          label="矫正视力左">
        </el-table-column>
        <el-table-column
          v-if="patientChartModalConfig.selectedType === PATIENT_CHART_TYPE.CORRECTED_EYE"
          align="center"
          prop="corrected_eye_right_vision"
          label="矫正视力右">
        </el-table-column>
        <el-table-column
          v-if="patientChartModalConfig.selectedType === PATIENT_CHART_TYPE.CYL"
          align="center"
          label="柱镜度R/L">
          <template slot-scope="{ row }">
            {{ getCylinderAxisPair(row) }}
          </template>
        </el-table-column>
        <el-table-column
          v-if="patientChartModalConfig.selectedType === PATIENT_CHART_TYPE.AXIS"
          align="center"
          prop="axisOs"
          label="轴位左">
          <template slot-scope="{ row }">
            {{ formatHealthRecordValue(row.axisOs, '°') }}
          </template>
        </el-table-column>
        <el-table-column
          v-if="patientChartModalConfig.selectedType === PATIENT_CHART_TYPE.AXIS"
          align="center"
          prop="axisOd"
          label="轴位右">
          <template slot-scope="{ row }">
            {{ formatHealthRecordValue(row.axisOd, '°') }}
          </template>
        </el-table-column>
        <el-table-column
          align="center"
          prop="source"
          label="来源">
        </el-table-column>
      </el-table>
    </app-modal>

  </div>
</template>

<script>
import echarts from 'echarts';
import {
  PATIENT_COLUMNS,
  ALLOWED_PERMISSION,
  PATIENT_ACTION,
  PATIENT_TYPE,
  PATIENT_TYPE_TEXT,
  PATIENT_TYPE_OPTIONS,
  CUSTOMER_TYPE,
  CUSTOMER_TYPE_TEXT,
  CUSTOMER_TYPE_OPTIONS,
  SEX,
  SEX_TEXT,
  SEX_OPTIONS,
  GENETIC_STATUS,
  GENETIC_STATUS_TEXT,
  GENETIC_STATUS_OPTIONS,
  EYES_POSITION,
  EYES_POSITION_TEXT,
  EYES_POSITION_OPTIONS,
  VISUAL_RANGE_OPTIONS,
} from './constants';
import PatientApi from './patient.api';
import DataTransferUtil from '@/utils/data-transfer.util';

const PATIENT_CHART_TYPE = {
  NAKED_EYE: 'nakedEye',
  DIOP: 'diop',
  AXIAL: 'axial',
  CORRECTED_EYE: 'correctedEye',
  CYL: 'cyl',
  AXIS: 'axis',
};

const PATIENT_CHART_TYPE_OPTIONS = [
  {
    label: '裸眼视力',
    value: PATIENT_CHART_TYPE.NAKED_EYE,
  },
  {
    label: '屈光度',
    value: PATIENT_CHART_TYPE.DIOP,
  },
  {
    label: '眼轴',
    value: PATIENT_CHART_TYPE.AXIAL,
  },
  {
    label: '矫正视力',
    value: PATIENT_CHART_TYPE.CORRECTED_EYE,
  },
  {
    label: '散光度',
    value: PATIENT_CHART_TYPE.CYL,
  },
];

const PATIENT_CHART_FIELD_MAP = {
  [PATIENT_CHART_TYPE.NAKED_EYE]: {
    leftField: 'naked_eye_left_vision',
    rightField: 'naked_eye_right_vision',
    leftLabel: '裸眼视力左',
    rightLabel: '裸眼视力右',
    yAxisName: '视力',
  },
  [PATIENT_CHART_TYPE.DIOP]: {
    leftField: 'diopOs',
    rightField: 'diopOd',
    leftLabel: '屈光度左',
    rightLabel: '屈光度右',
    yAxisName: '屈光度',
    inverseYAxis: true,
    symmetricYAxis: true,
  },
  [PATIENT_CHART_TYPE.AXIAL]: {
    leftField: 'axialOs',
    rightField: 'axialOd',
    leftLabel: '眼轴左',
    rightLabel: '眼轴右',
    yAxisName: '眼轴',
    yAxisMin: 21.0,
    yAxisMax: 25.0,
  },
  [PATIENT_CHART_TYPE.CORRECTED_EYE]: {
    leftField: 'corrected_eye_left_vision',
    rightField: 'corrected_eye_right_vision',
    leftLabel: '矫正视力左',
    rightLabel: '矫正视力右',
    yAxisName: '视力',
  },
  [PATIENT_CHART_TYPE.CYL]: {
    leftField: 'cylOs',
    rightField: 'cylOd',
    leftLabel: '散光度左',
    rightLabel: '散光度右',
    yAxisName: '散光度',
    yAxisMin: -8,
    yAxisMax: 0,
    inverseYAxis: true,
  },
  [PATIENT_CHART_TYPE.AXIS]: {
    leftField: 'axisOs',
    rightField: 'axisOd',
    leftLabel: '轴位左',
    rightLabel: '轴位右',
    yAxisName: '轴位',
  },
};

export default {
  name: 'patient',
  computed: {
    queryConfig() {
      return [
        {
          fieldName: 'patientType',
          name: '患者类型',
          type: 'select',
          options: PATIENT_TYPE_OPTIONS,
        },
        {
          fieldName: 'type',
          name: '客户类型',
          type: 'select',
          options: CUSTOMER_TYPE_OPTIONS,
        },
        {
          fieldName: 'sex',
          name: '性别',
          type: 'select',
          options: SEX_OPTIONS,
        },
        {
          fieldName: 'ageRange',
          name: '年龄范围',
          type: 'slot',
        },
        {
          fieldName: 'visualRange',
          name: '视力范围',
          type: 'slot',
        },
        {
          fieldName: 'geneticStatus',
          name: '遗传史',
          type: 'select',
          options: GENETIC_STATUS_OPTIONS,
        },
        {
          fieldName: 'demand',
          name: '诉求',
          type: 'select',
          options: this.patientDemandOptions,
        },
        {
          fieldName: 'treatmentHistory',
          name: '治疗史',
          type: 'select',
          options: this.treatmentHistoryOptions,
        },
        {
          fieldName: 'eyesPosition',
          name: '眼位',
          type: 'select',
          options: EYES_POSITION_OPTIONS,
        },
        {
          fieldName: 'nickName',
          name: '姓名',
        },
        {
          fieldName: 'mobile',
          name: '联系方式',
        },
        {
          fieldName: 'nickId',
          name: '用户ID',
        },
      ];
    },
    lineChartHeight() {
      return '600px';
    },
    selectedPatientChartData() {
      const chartConfig = PATIENT_CHART_FIELD_MAP[this.patientChartModalConfig.selectedType];
      if (!chartConfig) {
        return [];
      }
      return this.patientChartModalConfig.patientLogData
        .filter((item) => !this.isEmptyChartValue(item[chartConfig.leftField])
          || !this.isEmptyChartValue(item[chartConfig.rightField]));
    },
  },
  data() {
    return {
      PATIENT_COLUMNS,
      ALLOWED_PERMISSION,
      PATIENT_ACTION,
      PATIENT_CHART_TYPE,
      PATIENT_CHART_TYPE_OPTIONS,
      SEX_TEXT,
      PATIENT_TYPE_TEXT,
      CUSTOMER_TYPE_TEXT,
      GENETIC_STATUS_TEXT,
      EYES_POSITION_TEXT,
      VISUAL_RANGE_OPTIONS,
      queryData: {
        page: 1,
        limit: 10,
        count: 0,
        patientType: PATIENT_TYPE.ALL,
        type: CUSTOMER_TYPE.ALL,
        sex: SEX.ALL,
        geneticStatus: GENETIC_STATUS.ALL,
        demand: '',
        treatmentHistory: '',
        eyesPosition: EYES_POSITION.ALL,
        minAge: null,
        maxAge: null,
        minVisual: '',
        maxVisual: '',
      },
      patients: [],
      patientDemandOptions: [],
      patientDemand: {},
      treatmentHistoryOptions: [],
      treatmentHistory: {},
      patientChartModalConfig: {
        visible: false,
        nickName: '',
        selectedType: PATIENT_CHART_TYPE.NAKED_EYE,
        patientLogData: [],
      },
      patientChartResizeHandler: null,
    };
  },
  created() {
    this.fetchPatients();
    this.fetchPatientDemand();
  },
  beforeDestroy() {
    if (this.patientChartResizeHandler) {
      window.removeEventListener('resize', this.patientChartResizeHandler);
    }
  },
  methods: {
    async fetchPatients() {
      const {
        page,
        limit,
        patientType,
        type,
        sex,
        geneticStatus,
        demand,
        treatmentHistory,
        eyesPosition,
        minAge,
        maxAge,
        minVisual,
        maxVisual,
        mobile,
        nickName,
        nickId,
      } = this.queryData;

      const {
        count = 0,
        results = [],
      } = await PatientApi.fetchPatients(
        page,
        limit,
        patientType,
        geneticStatus,
        eyesPosition,
        sex,
        type,
        minAge,
        maxAge,
        minVisual,
        maxVisual,
        demand,
        treatmentHistory,
        mobile,
        nickName,
        nickId,
      );
      this.patients = results;
      this.$set(this.queryData, 'count', count);
    },
    async fetchPatientDemand() {
      const {
        self_describe: patientDemand,
        cure_history: treatmentHistory,
      } = await PatientApi.fetchPatientDemand();
      this.patientDemand = patientDemand;
      this.treatmentHistory = treatmentHistory;
      this.patientDemandOptions = [{ label: '全部', value: '' }].concat(Object.keys(patientDemand)
        .map((k) => ({ label: patientDemand[k], value: k })));
      this.treatmentHistoryOptions = Object.keys(treatmentHistory)
        .map((k) => ({ label: treatmentHistory[k], value: k }));
    },
    getPatientDemandTexts(demandKeyStr) {
      const demands = demandKeyStr?.split(',');
      return demands.map((demand) => this.patientDemand[demand]).join(',');
    },
    getTreatmentHistoryTexts(treatmentHistoryKeyStr) {
      if (treatmentHistoryKeyStr === null || treatmentHistoryKeyStr === undefined) {
        return '';
      }
      const treatments = treatmentHistoryKeyStr?.split(',');
      return treatments.map((treatment) => this.treatmentHistory[treatment]).join(',');
    },
    parseHealthRecord(healthRecord) {
      if (!healthRecord) {
        return {};
      }
      if (typeof healthRecord === 'object') {
        return healthRecord;
      }
      try {
        return JSON.parse(healthRecord);
      } catch (e) {
        return {};
      }
    },
    formatHealthRecordValue(value, unit = '') {
      if (value === undefined || value === null || value === '' || value === '--') {
        return '--';
      }
      const text = String(value).trim();
      if (!unit) {
        return text;
      }
      if (unit === 'D') {
        const unitlessText = text.replace(/D$/i, '');
        const numericValue = Number(unitlessText);
        if (!Number.isNaN(numericValue) && unitlessText !== '') {
          return `${numericValue > 0 ? '+' : ''}${numericValue.toFixed(2)}D`;
        }
      }
      return text.toLowerCase().endsWith(unit.toLowerCase()) ? text : `${text}${unit}`;
    },
    getHealthRecordPair(healthRecord, rightKey, leftKey, unit = '') {
      const record = this.parseHealthRecord(healthRecord);
      const rightValue = this.formatHealthRecordValue(record[rightKey], unit);
      const leftValue = this.formatHealthRecordValue(record[leftKey], unit);
      return `${rightValue}/${leftValue}`;
    },
    formatCylinderAxisValue(cylValue, axisValue) {
      const formattedCylinder = this.formatHealthRecordValue(cylValue, 'D').replace(/D$/i, '');
      const formattedAxis = this.formatHealthRecordValue(axisValue, '°').replace(/°$/, '');
      if (formattedCylinder === '--' && formattedAxis === '--') {
        return '--';
      }
      return `${formattedCylinder}×${formattedAxis}`;
    },
    getCylinderAxisPair(healthRecord) {
      const record = this.parseHealthRecord(healthRecord);
      const rightValue = this.formatCylinderAxisValue(record.cylOd, record.axisOd);
      const leftValue = this.formatCylinderAxisValue(record.cylOs, record.axisOs);
      return `${rightValue}/${leftValue}`;
    },
    getPupilDistance(healthRecord) {
      const record = this.parseHealthRecord(healthRecord);
      const val = record.pupilDistance ? record.pupilDistance : '--';
      return `${val}`;
    },
    async onExport() {
      const {
        page,
        limit,
        patientType,
        type,
        sex,
        geneticStatus,
        demand,
        eyesPosition,
        minAge,
        maxAge,
        minVisual,
        maxVisual,
      } = this.queryData;
      const key = await PatientApi.export({
        page,
        limit,
        patient_type: patientType,
        is_genetic: geneticStatus,
        eye_position: eyesPosition,
        sex,
        type,
        min_age: minAge,
        max_age: maxAge,
        min_naked_eye_vision: minVisual,
        max_naked_eye_vision: maxVisual,
        self_describe: demand,
      });
      await DataTransferUtil.downloadFileBySecretKey(key);
    },
    async  onExportAll() {
      const key = await PatientApi.export();
      await DataTransferUtil.downloadFileBySecretKey(key);
    },

    async handlePatientChart(row) {
      const result = await PatientApi.fetchStatPatientLog(row.customer_id);
      if (result && result.length > 0) {
        this.patientChartModalConfig.visible = true;
        this.patientChartModalConfig.nickName = row.nick_name;
        this.patientChartModalConfig.selectedType = PATIENT_CHART_TYPE.NAKED_EYE;
        this.convertLogData(result);
      } else {
        this.$Message.warning('暂无既往视力');
      }
    },

    handlePatientChartTypeChange(type) {
      this.patientChartModalConfig.selectedType = type;
      this.renderSelectedPatientChart();
    },

    isEmptyChartValue(value) {
      return value === undefined || value === null || value === '' || value === '--';
    },

    parseChartNumber(value) {
      if (this.isEmptyChartValue(value)) {
        return null;
      }
      const numericValue = Number(String(value).replace(/[Dmm°]/gi, '').trim());
      return Number.isNaN(numericValue) ? null : numericValue;
    },

    getChartSeriesValue(value) {
      const numericValue = this.parseChartNumber(value);
      if (numericValue === null) {
        return value;
      }
      return numericValue;
    },

    getYAxisOption(chartConfig, leftData, rightData) {
      const yAxis = {
        type: 'value',
        name: chartConfig.yAxisName,
        min: chartConfig.yAxisMin,
        max: chartConfig.yAxisMax,
        inverse: chartConfig.inverseYAxis,
      };

      if (chartConfig.symmetricYAxis) {
        const chartValues = leftData.concat(rightData)
          .map((value) => this.parseChartNumber(value))
          .filter((value) => value !== null);
        const maxAbsoluteValue = chartValues.length
          ? Math.max(...chartValues.map((value) => Math.abs(value)))
          : 1;
        const yAxisMax = Math.max(Math.ceil(maxAbsoluteValue), 1);

        yAxis.min = -yAxisMax;
        yAxis.max = yAxisMax;
      }

      return yAxis;
    },

    convertLogData(list) {
      const patientLogData = [];

      for (let i = 0; i < list.length; i += 1) {
        const item = list[i];
        const createTime = item.create_time;
        const record = this.parseHealthRecord(item.health_record);

        const patientLogItem = {
          create_time: createTime,
          naked_eye_left_vision: record.naked_eye_left_vision,
          naked_eye_right_vision: record.naked_eye_right_vision,
          diopOd: record.diopOd,
          diopOs: record.diopOs,
          axialOd: record.axialOd,
          axialOs: record.axialOs,
          corrected_eye_left_vision: record.corrected_eye_left_vision,
          corrected_eye_right_vision: record.corrected_eye_right_vision,
          cylOd: record.cylOd,
          cylOs: record.cylOs,
          axisOd: record.axisOd,
          axisOs: record.axisOs,
          source: item.create_by_group_type === 'sgs' ? '视光师更新' : '消费者更新',
        };

        patientLogData.push(patientLogItem);
      }

      this.patientChartModalConfig.patientLogData = patientLogData;
      this.renderSelectedPatientChart();
    },

    renderSelectedPatientChart() {
      const {
        selectedType,
      } = this.patientChartModalConfig;
      const chartConfig = PATIENT_CHART_FIELD_MAP[selectedType];

      if (!chartConfig) {
        return;
      }

      const chartData = this.selectedPatientChartData;
      const xData = chartData.map((item) => item.create_time);
      const leftData = chartData.map((item) => this.getChartSeriesValue(
        item[chartConfig.leftField],
      ));
      const rightData = chartData.map((item) => this.getChartSeriesValue(
        item[chartConfig.rightField],
      ));

      const option = {
        title: {
          text: '',
        },
        tooltip: {
          trigger: 'axis',
        },
        legend: {
          data: [chartConfig.leftLabel, chartConfig.rightLabel],
          align: 'right',
        },
        grid: {
          left: '5%',
          right: '5%',
          bottom: '5%',
          containLabel: true,
        },
        toolbox: {
          feature: {
          },
        },
        xAxis: {
          type: 'category',
          boundaryGap: true,
          data: xData,
        },
        yAxis: this.getYAxisOption(chartConfig, leftData, rightData),
        series: [
          {
            name: chartConfig.leftLabel,
            type: 'line',
            data: leftData,
          },
          {
            name: chartConfig.rightLabel,
            type: 'line',
            data: rightData,
          },
        ],
      };
      // console.log(option);

      this.$nextTick(() => {
        this.generatePatientChart(option);
      });
    },

    generatePatientChart(option) {
      const chartDom = document.getElementById('patientChart');
      if (!chartDom) {
        return;
      }
      const patientChart = echarts.getInstanceByDom(chartDom) || echarts.init(chartDom);
      patientChart.setOption(option, true);
      // 让图表跟随屏幕自动的去适应
      if (this.patientChartResizeHandler) {
        window.removeEventListener('resize', this.patientChartResizeHandler);
      }
      this.patientChartResizeHandler = () => {
        patientChart.resize();
      };
      window.addEventListener('resize', this.patientChartResizeHandler);
    },
  },
};
</script>

<style lang="less" scoped>
.age-range > div,
.visual-range > div {
  display: flex;
  align-items: center;
  > div {
    margin-right: 4px;
  }
}

.eyes-position,
.patient-demand,
.treatment-history {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.patient-chart-type-selector {
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 12px;
}
</style>
